/**
 * UserProvisioningTab Component
 * 
 * Admin panel tab for user provisioning and role management (Permission System V2)
 * 
 * Features:
 * - Provision new users with email + role
 * - View all users with their roles
 * - Update user primary roles
 * - Add/remove secondary roles
 * 
 * Created: 2026-01-26
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import { getRoleColor } from '../../hooks/usePermissions';
import useTableSort from '../../hooks/useTableSort';
import { FilterTemplateDropdown, FilterIcon, useFilterTemplate } from '../common/FilteringTemplate';
import { isExternalEmail } from '../../utils/menuAccess';

const EXTERNAL_ROLE_ID = 'external';

/**
 * Roles an e-mail may carry. An outside address gets External only;
 * an LGL address never gets External. Keeps the shape { deptName: roles[] }.
 */
const rolesForEmail = (rolesByDepartment, email) => {
  if (!email || !email.includes('@')) return rolesByDepartment;
  const external = isExternalEmail(email);
  const out = {};
  Object.entries(rolesByDepartment).forEach(([deptName, roles]) => {
    const kept = roles.filter(r => (r.id === EXTERNAL_ROLE_ID) === external);
    if (kept.length) out[deptName] = kept;
  });
  return out;
};

/**
 * Sub-Departments Tab Component
 * Allows managing user's sub-department (trade) assignments
 * Shows departments based on user's primary + secondary roles
 */
const SubDepartmentsTab = ({
  user,
  trades,
  subDepartments,
  loadingTrades,
  instance,
  accounts,
  showToast,
  onRefresh,
  styles
}) => {
  const [allDepartments, setAllDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedSubDepts, setSelectedSubDepts] = useState(new Set());

  // Initialize selected sub-departments from user's current trades
  useEffect(() => {
    const currentIds = new Set(trades.filter(t => !t.project_id).map(t => t.sub_department_id));
    setSelectedSubDepts(currentIds);
  }, [trades]);

  // Fetch all departments with their sub-departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await authenticatedApiCall(
          instance, accounts, 'GET',
          '/role-templates/departments'
        );
        if (response.data.success) {
          setAllDepartments(response.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, [instance, accounts]);

  // Get department IDs the user has access to (primary + secondary roles)
  const userDepartmentIds = useMemo(() => {
    const ids = new Set();
    // Primary role department
    if (user.role_department_id || user.department_id) {
      ids.add(user.role_department_id || user.department_id);
    }
    // Secondary roles departments
    if (user.secondary_roles && Array.isArray(user.secondary_roles)) {
      user.secondary_roles.forEach(sr => {
        if (sr.department_id) {
          ids.add(sr.department_id);
        }
      });
    }
    return ids;
  }, [user]);

  // Filter departments to only show those the user has roles in
  const accessibleDepartments = useMemo(() => {
    // Superadmin sees all
    if (user.primary_role_id === 'superadmin') {
      return allDepartments;
    }
    return allDepartments.filter(dept => userDepartmentIds.has(dept.id));
  }, [allDepartments, userDepartmentIds, user.primary_role_id]);

  // Toggle sub-department selection
  // NOTE: We update local state optimistically and intentionally do NOT call
  // onRefresh() here. Refreshing on every click re-fetches trades and flips
  // loadingTrades, which makes the whole tab flicker/"refresh" on each toggle.
  const toggleSubDept = async (subDeptId) => {
    const isSelected = selectedSubDepts.has(subDeptId);

    try {
      setSaving(true);

      if (isSelected) {
        // Remove the sub-department
        await authenticatedApiCall(
          instance, accounts, 'DELETE',
          `/project-assignments/trades/${user.id}/${subDeptId}`
        );
        setSelectedSubDepts(prev => {
          const next = new Set(prev);
          next.delete(subDeptId);
          return next;
        });
        showToast('Sub-department removed', 'success');
      } else {
        // Add the sub-department
        await authenticatedApiCall(
          instance, accounts, 'POST',
          '/project-assignments/trades',
          { userId: user.id, sub_department_id: subDeptId, projectId: null }
        );
        setSelectedSubDepts(prev => {
          const next = new Set(prev);
          next.add(subDeptId);
          return next;
        });
        showToast('Sub-department added', 'success');
      }
    } catch (err) {
      console.error('Error updating sub-department:', err);
      showToast('Failed to update sub-department', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Select-all / deselect-all for a single department.
  // If every sub-department in the group is already selected, this clears them;
  // otherwise it selects the ones that are missing.
  const toggleSelectAllForDept = async (dept) => {
    const subDepts = dept.sub_departments || [];
    if (subDepts.length === 0) return;

    const allSelected = subDepts.every(sd => selectedSubDepts.has(sd.id));
    // When everything is selected -> remove all; otherwise -> add the missing ones
    const targets = allSelected
      ? subDepts
      : subDepts.filter(sd => !selectedSubDepts.has(sd.id));

    try {
      setSaving(true);

      if (allSelected) {
        await Promise.all(targets.map(sd =>
          authenticatedApiCall(
            instance, accounts, 'DELETE',
            `/project-assignments/trades/${user.id}/${sd.id}`
          )
        ));
        setSelectedSubDepts(prev => {
          const next = new Set(prev);
          targets.forEach(sd => next.delete(sd.id));
          return next;
        });
        showToast('All sub-departments removed', 'success');
      } else {
        await Promise.all(targets.map(sd =>
          authenticatedApiCall(
            instance, accounts, 'POST',
            '/project-assignments/trades',
            { userId: user.id, sub_department_id: sd.id, projectId: null }
          )
        ));
        setSelectedSubDepts(prev => {
          const next = new Set(prev);
          targets.forEach(sd => next.add(sd.id));
          return next;
        });
        showToast('All sub-departments added', 'success');
      }
    } catch (err) {
      console.error('Error updating sub-departments:', err);
      showToast('Failed to update sub-departments', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loadingTrades) {
    return <div style={{ textAlign: 'center', color: '#666' }}>Loading sub-departments...</div>;
  }

  // Count by department
  const getSelectedCountForDept = (dept) => {
    return (dept.sub_departments || []).filter(sd => selectedSubDepts.has(sd.id)).length;
  };

  return (
    <div>
      <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
        Select which sub-departments/trades this user can access. 
        Sub-departments determine which modules and data the user can work with.
      </p>
      
      {selectedSubDepts.size === 0 && (
        <div style={{
          padding: '10px 15px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '6px',
          marginBottom: '15px',
          fontSize: '13px'
        }}>
          No sub-departments assigned. User will have limited module access.
        </div>
      )}

      {accessibleDepartments.length === 0 && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          textAlign: 'center',
          color: '#666'
        }}>
          <p style={{ margin: '0 0 8px 0' }}>No departments available.</p>
          <p style={{ margin: 0, fontSize: '12px' }}>
            Assign a primary or secondary role to enable sub-department selection.
          </p>
        </div>
      )}

      {accessibleDepartments.map(dept => {
        const deptSubDepts = dept.sub_departments || [];
        const allSelected = deptSubDepts.length > 0 && deptSubDepts.every(sd => selectedSubDepts.has(sd.id));
        const someSelected = deptSubDepts.some(sd => selectedSubDepts.has(sd.id));
        return (
        <div key={dept.id} style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            paddingBottom: '4px',
            borderBottom: '1px solid #eee'
          }}>
            <strong style={{ fontSize: '14px' }}>{dept.name}</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {getSelectedCountForDept(dept)} of {deptSubDepts.length} selected
              </span>
              {deptSubDepts.length > 0 && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '12px',
                  color: '#007bff',
                  fontWeight: 500,
                  cursor: saving ? 'wait' : 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                    onChange={() => toggleSelectAllForDept(dept)}
                    disabled={saving}
                    style={{ cursor: saving ? 'wait' : 'pointer' }}
                  />
                  Select all
                </label>
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {(dept.sub_departments || []).map(sd => {
              const isSelected = selectedSubDepts.has(sd.id);
              return (
                <label
                  key={sd.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: isSelected ? '#e7f3ff' : '#f8f9fa',
                    border: isSelected ? '2px solid #007bff' : '2px solid transparent',
                    borderRadius: '6px',
                    cursor: saving ? 'wait' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                    transition: 'all 0.15s ease'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSubDept(sd.id)}
                    disabled={saving}
                    style={{ cursor: saving ? 'wait' : 'pointer' }}
                  />
                  <span style={{ fontSize: '13px', fontWeight: isSelected ? '500' : '400' }}>
                    {sd.name}
                  </span>
                </label>
              );
            })}
            {(!dept.sub_departments || dept.sub_departments.length === 0) && (
              <span style={{ color: '#999', fontSize: '12px', fontStyle: 'italic' }}>
                No sub-departments defined
              </span>
            )}
          </div>
        </div>
        );
      })}

      {/* Show project-specific assignments if any */}
      {trades.filter(t => t.project_id).length > 0 && (
        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
          <strong style={{ fontSize: '14px', color: '#666' }}>Project-Specific Assignments:</strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
            {trades.filter(t => t.project_id).map(t => (
              <span
                key={t.id}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#e7f3ff',
                  color: '#004085',
                  borderRadius: '16px',
                  fontSize: '12px'
                }}
              >
                {t.sub_department_name} (Project #{t.project_id})
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Enhanced Edit Modal Component
 * Includes: role management, overrides, trades, audit history
 */
const EnhancedEditModal = ({
  user,
  roleTemplates,
  rolesByDepartment,
  departments,
  divisions,
  styles,
  onClose,
  onUpdateRole,
  onRefresh,
  instance,
  accounts,
  showToast,
  getRoleColor
}) => {
  const [activeTab, setActiveTab] = useState('role');
  const [overrides, setOverrides] = useState([]);
  const [trades, setTrades] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [authorityLevels, setAuthorityLevels] = useState([]);
  const [selectedAuthorityLevel, setSelectedAuthorityLevel] = useState(user.authority_level_id || null);
  const [selectedSecondaryRole, setSelectedSecondaryRole] = useState('');
  const [selectedSecondaryAuthority, setSelectedSecondaryAuthority] = useState('');
  const [addingSecondaryRole, setAddingSecondaryRole] = useState(false);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [userEstimates, setUserEstimates] = useState([]);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  // Employee Link tab state
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [empSearching, setEmpSearching] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(user.lt_employee_code || null);
  const [selectedEmpName, setSelectedEmpName] = useState(user.lt_employee_name || '');
  const [savingEmpLink, setSavingEmpLink] = useState(false);
  const empSearchTimer = useRef(null);
  const [savingAuthority, setSavingAuthority] = useState(false);
  // Division state
  const [selectedDivision, setSelectedDivision] = useState(user.division_id ? String(user.division_id) : '');
  const [savingDivision, setSavingDivision] = useState(false);

  // Employee code link state
  const [empCodeSearch, setEmpCodeSearch] = useState('');
  const [empCodeSearchResults, setEmpCodeSearchResults] = useState([]);
  const [empCodeSearchLoading, setEmpCodeSearchLoading] = useState(false);
  const [selectedEmpCode, setSelectedEmpCode] = useState(null); // { emp_code, emp_name }
  const [savingEmpCode, setSavingEmpCode] = useState(false);
  const [currentEmpCode, setCurrentEmpCode] = useState(user.lt_employee_code || null);
  const [currentEmpName, setCurrentEmpName] = useState(user.lt_employee_name || null);

  // Fetch authority levels on mount
  useEffect(() => {
    const fetchAuthorityLevels = async () => {
      try {
        const response = await authenticatedApiCall(
          instance, accounts, 'GET',
          '/role-templates/authority-levels'
        );
        if (response.data.success) {
          setAuthorityLevels(response.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching authority levels:', err);
      }
    };
    fetchAuthorityLevels();
  }, [instance, accounts]);

  // Update authority level
  const handleAuthorityLevelChange = async (newAuthorityLevelId) => {
    try {
      setSavingAuthority(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/users/${user.id}/authority-level`,
        { authority_level_id: newAuthorityLevelId === '' ? null : parseInt(newAuthorityLevelId) }
      );
      if (response.data.success) {
        setSelectedAuthorityLevel(newAuthorityLevelId === '' ? null : parseInt(newAuthorityLevelId));
        showToast('Authority level updated', 'success');
        onRefresh();
      }
    } catch (err) {
      console.error('Error updating authority level:', err);
      showToast('Failed to update authority level', 'error');
    } finally {
      setSavingAuthority(false);
    }
  };

  // Save division assignment
  const handleDivisionChange = async (newDivisionId) => {
    try {
      setSavingDivision(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/users/${user.id}/division`,
        { division_id: newDivisionId === '' ? null : parseInt(newDivisionId) }
      );
      if (response.data.success) {
        setSelectedDivision(newDivisionId);
        showToast(newDivisionId ? 'Company updated' : 'Company cleared', 'success');
        onRefresh();
      }
    } catch (err) {
      console.error('Error updating division:', err);
      showToast('Failed to update company', 'error');
    } finally {
      setSavingDivision(false);
    }
  };

  // Search employees for the employee code link
  const handleEmpCodeSearch = async (searchTerm) => {
    setEmpCodeSearch(searchTerm);
    setSelectedEmpCode(null);
    if (!searchTerm || searchTerm.trim().length < 2) {
      setEmpCodeSearchResults([]);
      return;
    }
    try {
      setEmpCodeSearchLoading(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/lt-employee/search?q=${encodeURIComponent(searchTerm.trim())}&limit=10`
      );
      if (response.data.success) {
        setEmpCodeSearchResults(response.data.data || []);
      }
    } catch (err) {
      console.error('Error searching employees:', err);
      setEmpCodeSearchResults([]);
    } finally {
      setEmpCodeSearchLoading(false);
    }
  };

  // Save (link) the selected employee code to this user
  const handleLinkEmpCode = async () => {
    if (!selectedEmpCode) return;
    try {
      setSavingEmpCode(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/users/${user.id}/employee-code`,
        { lt_employee_code: selectedEmpCode.emp_code }
      );
      if (response.data.success) {
        setCurrentEmpCode(selectedEmpCode.emp_code);
        setCurrentEmpName(selectedEmpCode.emp_name);
        setSelectedEmpCode(null);
        setEmpCodeSearch('');
        setEmpCodeSearchResults([]);
        showToast(`Employee code ${selectedEmpCode.emp_code} linked`, 'success');
        onRefresh();
      }
    } catch (err) {
      console.error('Error linking employee code:', err);
      showToast(err.response?.data?.message || 'Failed to link employee code', 'error');
    } finally {
      setSavingEmpCode(false);
    }
  };

  // Unlink the employee code from this user
  const handleUnlinkEmpCode = async () => {
    if (!window.confirm('Unlink this employee code? The Fab Shop Foreman check will fall back to email matching.')) return;
    try {
      setSavingEmpCode(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/users/${user.id}/employee-code`,
        { lt_employee_code: null }
      );
      if (response.data.success) {
        setCurrentEmpCode(null);
        setCurrentEmpName(null);
        showToast('Employee code unlinked', 'success');
        onRefresh();
      }
    } catch (err) {
      console.error('Error unlinking employee code:', err);
      showToast(err.response?.data?.message || 'Failed to unlink employee code', 'error');
    } finally {
      setSavingEmpCode(false);
    }
  };

  // Add secondary role for cross-department access
  const handleAddSecondaryRole = async () => {
    if (!selectedSecondaryRole) return;
    
    try {
      setAddingSecondaryRole(true);
      const payload = { 
        role_id: selectedSecondaryRole,
        authority_level_id: selectedSecondaryAuthority ? parseInt(selectedSecondaryAuthority) : null
      };
      const response = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/users/${user.id}/secondary-roles`,
        payload
      );
      
      if (response.data.success) {
        showToast('Secondary role added - assign sub-departments for module access', 'success');
        setSelectedSecondaryRole('');
        setSelectedSecondaryAuthority('');
        onRefresh();
      }
    } catch (err) {
      console.error('Error adding secondary role:', err);
      showToast(err.response?.data?.message || 'Failed to add secondary role', 'error');
    } finally {
      setAddingSecondaryRole(false);
    }
  };

  // Remove secondary role
  const handleRemoveSecondaryRole = async (roleId) => {
    if (!window.confirm('Remove this secondary role? User will lose access to that department.')) {
      return;
    }
    
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'DELETE',
        `/users/${user.id}/secondary-roles/${roleId}`
      );
      
      if (response.data.success) {
        showToast('Secondary role removed', 'success');
        onRefresh();
      }
    } catch (err) {
      console.error('Error removing secondary role:', err);
      showToast('Failed to remove secondary role', 'error');
    }
  };

  // Fetch overrides for user
  const fetchOverrides = useCallback(async () => {
    try {
      setLoadingOverrides(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/permissions/overrides/user/${user.id}`
      );
      if (response.data.success) {
        setOverrides(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching overrides:', err);
    } finally {
      setLoadingOverrides(false);
    }
  }, [instance, accounts, user.id]);

  // Fetch trades for user
  const fetchTrades = useCallback(async () => {
    try {
      setLoadingTrades(true);
      const [tradesRes, subDeptsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/project-assignments/trades/${user.id}`),
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates/departments')
      ]);
      if (tradesRes.data.success) {
        setTrades(tradesRes.data.data || []);
      }
      if (subDeptsRes.data.success) {
        // Get sub-departments from all departments
        const allSubDepts = [];
        for (const dept of subDeptsRes.data.data || []) {
          try {
            const subRes = await authenticatedApiCall(
              instance, accounts, 'GET',
              `/role-templates/departments/${dept.id}/sub-departments`
            );
            if (subRes.data.success) {
              allSubDepts.push(...(subRes.data.data || []));
            }
          } catch (e) {
            // Ignore - sub-departments API might not exist
          }
        }
        setSubDepartments(allSubDepts);
      }
    } catch (err) {
      console.error('Error fetching trades:', err);
    } finally {
      setLoadingTrades(false);
    }
  }, [instance, accounts, user.id]);

  // Fetch audit log for user
  const fetchAuditLog = useCallback(async () => {
    try {
      setLoadingAudit(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/audit-log?target_user_id=${user.id}&limit=20`
      );
      if (response.data.success) {
        setAuditLog(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
    } finally {
      setLoadingAudit(false);
    }
  }, [instance, accounts, user.id]);

  // Fetch projects for user
  const fetchProjects = useCallback(async () => {
    try {
      setLoadingProjects(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/project-assignments/user/${user.id}/projects`
      );
      if (response.data.success) {
        setUserProjects(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching user projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  }, [instance, accounts, user.id]);

  // Fetch estimates for user
  const fetchEstimates = useCallback(async () => {
    try {
      setLoadingEstimates(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/estimates/user/${user.id}`
      );
      if (response.data.success) {
        setUserEstimates(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching user estimates:', err);
    } finally {
      setLoadingEstimates(false);
    }
  }, [instance, accounts, user.id]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'overrides' && overrides.length === 0) {
      fetchOverrides();
    } else if (activeTab === 'trades' && trades.length === 0) {
      fetchTrades();
    } else if (activeTab === 'audit' && auditLog.length === 0) {
      fetchAuditLog();
    } else if (activeTab === 'projects' && userProjects.length === 0) {
      fetchProjects();
    } else if (activeTab === 'estimates' && userEstimates.length === 0) {
      fetchEstimates();
    }
  }, [activeTab, fetchOverrides, fetchTrades, fetchAuditLog, fetchProjects, fetchEstimates, overrides.length, trades.length, auditLog.length, userProjects.length, userEstimates.length]);

  // Employee Link: debounced search
  const handleEmpSearchChange = (value) => {
    setEmpSearch(value);
    if (empSearchTimer.current) clearTimeout(empSearchTimer.current);
    if (!value || value.trim().length < 1) {
      setEmpResults([]);
      return;
    }
    empSearchTimer.current = setTimeout(async () => {
      try {
        setEmpSearching(true);
        const res = await authenticatedApiCall(
          instance, accounts, 'GET',
          `/lt-employee/search?q=${encodeURIComponent(value.trim())}&limit=20`
        );
        if (res.data.success) {
          setEmpResults(res.data.data || []);
        }
      } catch (err) {
        console.error('Error searching employees:', err);
      } finally {
        setEmpSearching(false);
      }
    }, 300);
  };

  // Employee Link: save
  const handleSaveEmpLink = async () => {
    try {
      setSavingEmpLink(true);
      const res = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/users/${user.id}/employee-code`,
        { lt_employee_code: selectedEmp || null }
      );
      if (res.data.success) {
        showToast(selectedEmp ? 'Employee link saved' : 'Employee link cleared', 'success');
        onRefresh();
      }
    } catch (err) {
      console.error('Error saving employee link:', err);
      showToast('Failed to save employee link', 'error');
    } finally {
      setSavingEmpLink(false);
    }
  };

  // Remove override
  const handleRemoveOverride = async (overrideId) => {
    if (!window.confirm('Remove this permission override?')) return;
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'DELETE',
        `/permissions/overrides/${overrideId}`
      );
      if (response.data.success) {
        showToast('Override removed', 'success');
        fetchOverrides();
      }
    } catch (err) {
      showToast('Failed to remove override', 'error');
    }
  };

  const tabStyle = (isActive) => ({
    padding: '10px 16px',
    border: 'none',
    backgroundColor: isActive ? '#007bff' : '#f8f9fa',
    color: isActive ? 'white' : '#333',
    cursor: 'pointer',
    borderRadius: '6px 6px 0 0',
    fontWeight: isActive ? '600' : '400',
    fontSize: '13px'
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div style={styles.modal} onClick={onClose}>
      <div 
        style={{...styles.modalContent, maxWidth: '700px', maxHeight: '85vh'}} 
        onClick={e => e.stopPropagation()}
      >
        <h3 style={styles.modalTitle}>
          {user.display_name || user.email}
        </h3>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '0', borderBottom: '2px solid #007bff' }}>
          <button style={tabStyle(activeTab === 'role')} onClick={() => setActiveTab('role')}>
            Role
          </button>
          <button style={tabStyle(activeTab === 'overrides')} onClick={() => setActiveTab('overrides')}>
            Overrides
          </button>
          <button style={tabStyle(activeTab === 'trades')} onClick={() => setActiveTab('trades')}>
            Sub-Departments
          </button>
          <button style={tabStyle(activeTab === 'projects')} onClick={() => setActiveTab('projects')}>
            Projects{userProjects.length > 0 ? ` (${userProjects.length})` : ''}
          </button>
          <button style={tabStyle(activeTab === 'estimates')} onClick={() => setActiveTab('estimates')}>
            Estimates{userEstimates.length > 0 ? ` (${userEstimates.length})` : ''}
          </button>
          <button style={tabStyle(activeTab === 'audit')} onClick={() => setActiveTab('audit')}>
            Audit Log
          </button>
          <button style={tabStyle(activeTab === 'employeeLink')} onClick={() => setActiveTab('employeeLink')}>
            Employee Link
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ padding: '20px 0', minHeight: '300px' }}>
          {/* Role Tab */}
          {activeTab === 'role' && (
            <div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="text"
                  style={{...styles.input, backgroundColor: '#f5f5f5'}}
                  value={user.email}
                  disabled
                />
              </div>

              {/* Employee Code Link */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Employee Code Link
                  <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                    (Required for Fab Shop Foreman multi-project assignment)
                  </span>
                </label>
                {currentEmpCode ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '6px 12px', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7',
                      borderRadius: '20px', fontSize: '13px', fontWeight: '600', color: '#065f46'
                    }}>
                      <span style={{ fontSize: '14px' }}>🔗</span>
                      {currentEmpCode}{currentEmpName ? ` — ${currentEmpName}` : ''}
                    </span>
                    <button
                      onClick={handleUnlinkEmpCode}
                      disabled={savingEmpCode}
                      style={{
                        padding: '5px 12px', border: '1px solid #fca5a5', borderRadius: '4px',
                        backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px'
                      }}
                    >
                      {savingEmpCode ? 'Saving...' : 'Unlink'}
                    </button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        style={{ ...styles.input, flex: 1 }}
                        value={selectedEmpCode ? `${selectedEmpCode.emp_code} — ${selectedEmpCode.emp_name}` : empCodeSearch}
                        onChange={(e) => handleEmpCodeSearch(e.target.value)}
                        placeholder="Search by name or employee code..."
                      />
                      <button
                        onClick={handleLinkEmpCode}
                        disabled={!selectedEmpCode || savingEmpCode}
                        style={{
                          padding: '8px 16px', border: 'none', borderRadius: '4px',
                          backgroundColor: selectedEmpCode ? '#059669' : '#d1d5db',
                          color: selectedEmpCode ? 'white' : '#9ca3af',
                          cursor: selectedEmpCode ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '500'
                        }}
                      >
                        {savingEmpCode ? 'Saving...' : 'Link'}
                      </button>
                    </div>
                    {/* Search results dropdown */}
                    {empCodeSearchResults.length > 0 && !selectedEmpCode && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '200px', overflowY: 'auto'
                      }}>
                        {empCodeSearchResults.map(emp => (
                          <div
                            key={emp.emp_code}
                            onClick={() => {
                              setSelectedEmpCode({ emp_code: emp.emp_code, emp_name: emp.emp_name });
                              setEmpCodeSearch('');
                              setEmpCodeSearchResults([]);
                            }}
                            style={{
                              padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            <strong>{emp.emp_code}</strong> — {emp.emp_name}
                            {emp.trade && <span style={{ color: '#6b7280', marginLeft: '6px', fontSize: '12px' }}>{emp.trade}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {empCodeSearchLoading && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Searching...</div>
                    )}
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Not linked — search to connect this user to their employee record
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Current Role</label>
                <span style={styles.roleBadge(user.role_color || getRoleColor(user.primary_role_id))}>
                  {user.role_name || user.primary_role_id || 'No Role'}
                </span>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Change Primary Role</label>
                <select
                  style={styles.input}
                  defaultValue={user.primary_role_id || ''}
                  onChange={(e) => {
                    if (e.target.value && e.target.value !== user.primary_role_id) {
                      onUpdateRole(user.id, e.target.value);
                    }
                  }}
                >
                  <option value="">Select a role...</option>
                  {Object.entries(rolesForEmail(rolesByDepartment, user.email)).map(([deptName, roles]) => (
                    <optgroup key={deptName} label={deptName}>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.display_name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {isExternalEmail(user.email) && (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
                    External user. The role grants nothing by itself; give them an External template on a job under Permissions → Assignments.
                  </div>
                )}
              </div>
              
              {/* Authority Level - Decoupled from Role */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Authority Level
                  <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                    (Override role default)
                  </span>
                </label>
                <select
                  style={styles.input}
                  value={selectedAuthorityLevel || ''}
                  onChange={(e) => handleAuthorityLevelChange(e.target.value)}
                  disabled={savingAuthority}
                >
                  <option value="">Use Role Default ({user.role_default_authority || user.effective_authority_level || 'Staff'})</option>
                  {authorityLevels.map(al => (
                    <option key={al.id} value={al.id}>
                      {al.name} - {al.description}
                    </option>
                  ))}
                </select>
                {user.user_authority_level && (
                  <p style={{ fontSize: '12px', color: '#28a745', marginTop: '4px' }}>
                    Currently overridden to: {user.user_authority_level}
                  </p>
                )}
                {!user.user_authority_level && user.effective_authority_level && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Using role default: {user.effective_authority_level}
                  </p>
                )}
              </div>

              {/* Company / Division */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Company (Division)</label>
                <select
                  style={styles.input}
                  value={selectedDivision}
                  onChange={(e) => handleDivisionChange(e.target.value)}
                  disabled={savingDivision}
                >
                  <option value="">— Not assigned —</option>
                  {(divisions || []).map(div => (
                    <option key={div.id} value={String(div.id)}>
                      {div.division_number} - {div.division_name}
                    </option>
                  ))}
                </select>
                {savingDivision && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Saving...</p>
                )}
              </div>

              {/* Secondary Roles for Cross-Department Access */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Secondary Roles
                  <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                    (Cross-department access)
                  </span>
                </label>
                
                {/* Current Secondary Roles */}
                {user.secondary_roles && user.secondary_roles.length > 0 ? (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {user.secondary_roles.map((sr, idx) => (
                        <span 
                          key={idx} 
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 10px',
                            backgroundColor: '#e7f3ff',
                            border: '1px solid #b8daff',
                            borderRadius: '16px',
                            fontSize: '13px'
                          }}
                        >
                          <strong>{sr.role_name}</strong>
                          <span style={{ color: '#666', fontSize: '11px' }}>
                            ({sr.department_name || 'Unknown dept'})
                          </span>
                          {sr.authority_level_name && (
                            <span style={{ 
                              color: '#28a745', 
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              [{sr.authority_level_name}]
                            </span>
                          )}
                          {sr.expires_at && (
                            <span style={{ color: '#856404', fontSize: '11px' }}>
                              until {new Date(sr.expires_at).toLocaleDateString()}
                            </span>
                          )}
                          <button
                            onClick={() => handleRemoveSecondaryRole(sr.role_id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              padding: '0 2px',
                              fontSize: '16px',
                              lineHeight: 1
                            }}
                            title="Remove secondary role"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                    No secondary roles. Add roles from other departments for cross-department access.
                  </p>
                )}

                {/* Add Secondary Role */}
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  alignItems: 'flex-end',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                      Add role from another department:
                    </label>
                    <select
                      value={selectedSecondaryRole || ''}
                      onChange={(e) => setSelectedSecondaryRole(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    >
                      <option value="">Select a role...</option>
                      {Object.entries(rolesByDepartment)
                        .filter(([deptName]) => {
                          // Get user's primary role department
                          const primaryDeptId = user.role_department_id || user.department_id;
                          const dept = departments.find(d => d.name === deptName);
                          // Show other departments, not the primary one
                          return dept && dept.id !== primaryDeptId;
                        })
                        .map(([deptName, roles]) => (
                          <optgroup key={deptName} label={`── ${deptName} ──`}>
                            {roles
                              .filter(role => {
                                // Don't show roles already assigned as secondary
                                const alreadyAssigned = (user.secondary_roles || [])
                                  .some(sr => sr.role_id === role.id);
                                // Don't show superadmin, and never External as a second role
                                return !alreadyAssigned && role.id !== 'superadmin' && role.id !== EXTERNAL_ROLE_ID;
                              })
                              .map(role => (
                                <option key={role.id} value={role.id}>
                                  {role.display_name}
                                </option>
                              ))
                            }
                          </optgroup>
                        ))
                      }
                    </select>
                  </div>
                  <div style={{ flex: '0 1 150px', minWidth: '130px' }}>
                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                      Authority Level:
                    </label>
                    <select
                      value={selectedSecondaryAuthority || ''}
                      onChange={(e) => setSelectedSecondaryAuthority(e.target.value)}
                      disabled={!selectedSecondaryRole}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px',
                        backgroundColor: selectedSecondaryRole ? '#fff' : '#f5f5f5'
                      }}
                    >
                      <option value="">Use role default</option>
                      {authorityLevels.map(level => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleAddSecondaryRole}
                    disabled={!selectedSecondaryRole || addingSecondaryRole}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: selectedSecondaryRole ? '#28a745' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedSecondaryRole ? 'pointer' : 'not-allowed',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      alignSelf: 'flex-end'
                    }}
                  >
                    {addingSecondaryRole ? 'Adding...' : '+ Add Role'}
                  </button>
                </div>
                
                <p style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                  Secondary roles grant access to modules in other departments. 
                  After adding, assign sub-departments in the Sub-Departments tab.
                </p>
              </div>
            </div>
          )}

          {/* Overrides Tab */}
          {activeTab === 'overrides' && (
            <div>
              {loadingOverrides ? (
                <div style={{ textAlign: 'center', color: '#666' }}>Loading overrides...</div>
              ) : overrides.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                  No permission overrides for this user.
                  <p style={{ fontSize: '12px', marginTop: '10px' }}>
                    Use the Permission Overrides tab in Admin Panel to add overrides.
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  {/* minWidth so the columns keep legible widths and the wrapper scrolls,
                      instead of width:100% squeezing them all into a phone. */}
                  <table style={{ width: '100%', minWidth: 760, fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Permission</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Project</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Expires</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {overrides.map(o => (
                        <tr key={o.id}>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                            <code style={{ fontSize: '12px' }}>{o.permission}</code>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              backgroundColor: o.granted ? '#d4edda' : '#f8d7da',
                              color: o.granted ? '#155724' : '#721c24',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              {o.granted ? 'GRANT' : 'DENY'}
                            </span>
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                            {o.job_number || <span style={{ color: '#999' }}>Global</span>}
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                            {o.expires_at ? new Date(o.expires_at).toLocaleDateString() : 'Never'}
                          </td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                            <button
                              onClick={() => handleRemoveOverride(o.id)}
                              style={{
                                padding: '2px 8px',
                                fontSize: '11px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Sub-Departments Tab (formerly Trades) */}
          {activeTab === 'trades' && (
            <SubDepartmentsTab
              user={user}
              trades={trades}
              subDepartments={subDepartments}
              loadingTrades={loadingTrades}
              instance={instance}
              accounts={accounts}
              showToast={showToast}
              onRefresh={() => {
                fetchTrades();
                onRefresh();
              }}
              styles={styles}
            />
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div>
              {loadingProjects ? (
                <div style={{ textAlign: 'center', color: '#666' }}>Loading projects...</div>
              ) : userProjects.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                  No projects assigned to this user.
                </div>
              ) : (
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Job #</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Project Name</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Role Override</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Assigned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userProjects.map(project => (
                        <tr key={project.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '10px 12px', fontWeight: '500' }}>{project.job_number}</td>
                          <td style={{ padding: '10px 12px' }}>{project.job_name}</td>
                          <td style={{ padding: '10px 12px' }}>
                            {project.override_role_name ? (
                              <span style={{
                                padding: '2px 8px',
                                backgroundColor: '#fff3cd',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                {project.override_role_name}
                              </span>
                            ) : (
                              <span style={{ color: '#999', fontSize: '12px' }}>Default</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#666', fontSize: '12px' }}>
                            {project.assigned_at ? new Date(project.assigned_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p style={{ fontSize: '12px', color: '#999', marginTop: '12px', fontStyle: 'italic' }}>
                Project assignments are managed from each project's settings.
              </p>
            </div>
          )}

          {/* Estimates Tab */}
          {activeTab === 'estimates' && (
            <div>
              {loadingEstimates ? (
                <div style={{ textAlign: 'center', color: '#666' }}>Loading estimates...</div>
              ) : userEstimates.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                  No estimates assigned to this user.
                </div>
              ) : (
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Estimate #</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Estimate Name</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Status</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Assigned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userEstimates.map(estimate => (
                        <tr key={estimate.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '10px 12px', fontWeight: '500' }}>{estimate.estimate_number}</td>
                          <td style={{ padding: '10px 12px' }}>{estimate.estimate_name}</td>
                          <td style={{ padding: '10px 12px' }}>
                            {estimate.awarded ? (
                              <span style={{
                                padding: '2px 8px',
                                backgroundColor: '#d4edda',
                                color: '#155724',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                Awarded{estimate.job_number ? ` (${estimate.job_number})` : ''}
                              </span>
                            ) : estimate.archived_at ? (
                              <span style={{
                                padding: '2px 8px',
                                backgroundColor: '#f1f1f1',
                                color: '#888',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                Archived
                              </span>
                            ) : (
                              <span style={{
                                padding: '2px 8px',
                                backgroundColor: '#e2e3f3',
                                color: '#3b3f8c',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                Active
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#666', fontSize: '12px' }}>
                            {estimate.assigned_at ? new Date(estimate.assigned_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p style={{ fontSize: '12px', color: '#999', marginTop: '12px', fontStyle: 'italic' }}>
                Estimate assignments are managed from each estimate's settings.
              </p>
            </div>
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <div>
              {loadingAudit ? (
                <div style={{ textAlign: 'center', color: '#666' }}>Loading audit history...</div>
              ) : auditLog.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                  No audit log entries for this user.
                </div>
              ) : (
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {auditLog.map(entry => (
                    <div
                      key={entry.id}
                      style={{
                        padding: '12px',
                        borderBottom: '1px solid #eee',
                        fontSize: '13px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {entry.action_type?.replace(/_/g, ' ')}
                        </span>
                        <span style={{ color: '#666', fontSize: '12px' }}>
                          {formatDate(entry.created_at)}
                        </span>
                      </div>
                      <div style={{ color: '#666', fontSize: '12px' }}>
                        By: {entry.performed_by_name || entry.performed_by_email || 'System'}
                      </div>
                      {entry.role_id && (
                        <div style={{ color: '#666', fontSize: '12px' }}>
                          Role: {entry.role_id}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Employee Link Tab */}
          {activeTab === 'employeeLink' && (
            <div>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                Link this portal user to their employee record in the time tracker.
                This is optional — users can be provisioned without a link and connected later.
              </p>

              {/* Current Link Status */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Current Employee Link</label>
                {selectedEmp ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 14px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '8px',
                    border: '1px solid #c8e6c9'
                  }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#2e7d32' }}>
                      {selectedEmp}
                    </span>
                    {selectedEmpName && (
                      <span style={{ fontSize: '13px', color: '#555' }}>{selectedEmpName}</span>
                    )}
                    <button
                      onClick={() => { setSelectedEmp(null); setSelectedEmpName(''); }}
                      style={{
                        marginLeft: 'auto',
                        padding: '3px 10px',
                        fontSize: '12px',
                        backgroundColor: '#fff',
                        border: '1px solid #ef9a9a',
                        borderRadius: '4px',
                        color: '#c62828',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <div style={{
                    padding: '10px 14px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#999',
                    border: '1px solid #e0e0e0'
                  }}>
                    No employee linked
                  </div>
                )}
              </div>

              {/* Search */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Search Employee (name or code)</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Search by name or code"
                  value={empSearch}
                  onChange={(e) => handleEmpSearchChange(e.target.value)}
                />
              </div>

              {/* Results */}
              {empSearching && (
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Searching...</div>
              )}
              {!empSearching && empResults.length > 0 && (
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '16px',
                  maxHeight: '220px',
                  overflowY: 'auto'
                }}>
                  {empResults.map(emp => (
                    <div
                      key={emp.emp_code}
                      onClick={() => {
                        setSelectedEmp(emp.emp_code);
                        setSelectedEmpName(emp.emp_name);
                        setEmpSearch('');
                        setEmpResults([]);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 14px',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        fontSize: '13px',
                        backgroundColor: selectedEmp === emp.emp_code ? '#e3f2fd' : 'white',
                        transition: 'background-color 0.1s'
                      }}
                      onMouseEnter={e => { if (selectedEmp !== emp.emp_code) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                      onMouseLeave={e => { if (selectedEmp !== emp.emp_code) e.currentTarget.style.backgroundColor = 'white'; }}
                    >
                      <span style={{ fontWeight: '600', minWidth: '60px', color: '#1565c0' }}>{emp.emp_code}</span>
                      <span style={{ flex: 1 }}>{emp.emp_name}</span>
                      {emp.trade && <span style={{ fontSize: '12px', color: '#888' }}>{emp.trade}</span>}
                      {emp.division && <span style={{ fontSize: '12px', color: '#aaa' }}>{emp.division}</span>}
                    </div>
                  ))}
                </div>
              )}
              {!empSearching && empSearch.length > 0 && empResults.length === 0 && (
                <div style={{ fontSize: '13px', color: '#999', marginBottom: '12px' }}>No employees found matching "{empSearch}"</div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSaveEmpLink}
                disabled={savingEmpLink}
                style={{
                  padding: '10px 24px',
                  backgroundColor: savingEmpLink ? '#aaa' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: savingEmpLink ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {savingEmpLink ? 'Saving...' : 'Save Employee Link'}
              </button>
            </div>
          )}
        </div>

        {/* Close Button */}
        <div style={styles.modalButtons}>
          <button
            type="button"
            style={styles.cancelBtn}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const UserProvisioningTab = () => {
  const { instance, accounts } = useMsal();
  const { toast, showToast } = useToast();
  
  // Data state
  const [users, setUsers] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'pending'
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Resend welcome email state
  const [resendingWelcome, setResendingWelcome] = useState(null); // user ID being resent
  
  // Provision form state
  const [provisionForm, setProvisionForm] = useState({
    email: '',
    primary_role_id: '',
    authority_level_id: '',
    division_id: '',
    lt_employee_code: '',
    reason: '',
    send_welcome_email: true
  });
  const [provisionSubmitting, setProvisionSubmitting] = useState(false);
  const [provisionAuthorityLevels, setProvisionAuthorityLevels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  // Employee code search for provision modal
  const [provisionEmpSearch, setProvisionEmpSearch] = useState('');
  const [provisionEmpResults, setProvisionEmpResults] = useState([]);
  const [provisionEmpLoading, setProvisionEmpLoading] = useState(false);
  const [provisionEmpSelected, setProvisionEmpSelected] = useState(null);

  // Fetch data
  const fetchData = useCallback(async (selectedUserId = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch in parallel
      const [usersRes, rolesRes, deptsRes, authLevelsRes, divisionsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/users/all'),
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates'),
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates/departments'),
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates/authority-levels'),
        authenticatedApiCall(instance, accounts, 'GET', '/divisions')
      ]);
      
      if (usersRes.data.success) {
        const freshUsers = usersRes.data.data || [];
        setUsers(freshUsers);
        // Update selectedUser with fresh data if modal is open
        if (selectedUserId) {
          const freshSelectedUser = freshUsers.find(u => u.id === selectedUserId);
          if (freshSelectedUser) {
            setSelectedUser(freshSelectedUser);
          }
        }
      }
      if (rolesRes.data.success) {
        setRoleTemplates(rolesRes.data.data || []);
      }
      if (deptsRes.data.success) {
        setDepartments(deptsRes.data.data || []);
      }
      if (authLevelsRes.data.success) {
        // Filter out System authority level - only superadmins should have that
        setProvisionAuthorityLevels((authLevelsRes.data.data || []).filter(al => al.name !== 'System'));
      }
      if (divisionsRes.data.success) {
        setDivisions(divisionsRes.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matches = 
          (user.email || '').toLowerCase().includes(search) ||
          (user.display_name || '').toLowerCase().includes(search) ||
          (user.first_name || '').toLowerCase().includes(search) ||
          (user.last_name || '').toLowerCase().includes(search) ||
          (user.role_name || '').toLowerCase().includes(search);
        if (!matches) return false;
      }
      
      // Department filter
      if (filterDepartment) {
        const dept = departments.find(d => d.id === parseInt(filterDepartment));
        if (dept && user.department_name !== dept.name) return false;
      }
      
      // Status filter
      if (filterStatus === 'pending' && user.azure_object_id) return false;
      if (filterStatus === 'active' && !user.azure_object_id) return false;
      
      return true;
    });
  }, [users, searchTerm, filterDepartment, filterStatus, departments]);

  const userColumns = useMemo(() => [
    { key: 'display_name', label: 'User', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'role_display', label: 'Role', type: 'text' },
    { key: 'authority_display', label: 'Authority', type: 'text' },
    { key: 'department_name', label: 'Department', type: 'text' },
    { key: 'lt_employee_code', label: 'Emp Code', type: 'text' },
    { key: 'status_display', label: 'Status', type: 'text' },
    { key: 'last_login_display', label: 'Last Login', type: 'date' },
  ], []);

  const userSort = useTableSort({ columns: userColumns });

  const flatUsers = useMemo(() => filteredUsers.map(u => ({
    ...u,
    display_name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Pending...',
    role_display: u.role_name || u.primary_role_id || 'No Role',
    authority_display: u.user_authority_level || u.authority_level || 'Staff',
    department_name: u.department_name || '',
    status_display: u.azure_object_id ? 'Active' : 'Pending',
    last_login_display: u.last_login ? new Date(u.last_login).toLocaleDateString() : '',
  })), [filteredUsers]);

  const userFilter = useFilterTemplate({ items: flatUsers, columns: userColumns });

  const sortedFilteredUsers = useMemo(() => {
    return userSort.applySorting(userFilter.filteredItems);
  }, [userFilter.filteredItems, userSort]);

  // Group role templates by department
  const rolesByDepartment = useMemo(() => {
    const grouped = {};
    roleTemplates.forEach(role => {
      const deptName = role.department_name || 'Cross-Department';
      if (!grouped[deptName]) grouped[deptName] = [];
      grouped[deptName].push(role);
    });
    // Sort roles within each department by hierarchy
    Object.values(grouped).forEach(roles => {
      roles.sort((a, b) => (a.hierarchy_level || 100) - (b.hierarchy_level || 100));
    });
    return grouped;
  }, [roleTemplates]);

  // Employee search for provision modal
  const handleProvisionEmpSearch = async (searchTerm) => {
    setProvisionEmpSearch(searchTerm);
    setProvisionEmpSelected(null);
    setProvisionForm(prev => ({ ...prev, lt_employee_code: '' }));
    if (!searchTerm || searchTerm.trim().length < 2) {
      setProvisionEmpResults([]);
      return;
    }
    try {
      setProvisionEmpLoading(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/lt-employee/search?q=${encodeURIComponent(searchTerm.trim())}&limit=10`
      );
      if (response.data.success) {
        setProvisionEmpResults(response.data.data || []);
      }
    } catch (err) {
      console.error('Error searching employees:', err);
      setProvisionEmpResults([]);
    } finally {
      setProvisionEmpLoading(false);
    }
  };

  // Provision new user
  const handleProvision = async (e) => {
    e.preventDefault();
    
    if (!provisionForm.email || !provisionForm.primary_role_id) {
      showToast('Please fill in email and role', 'error');
      return;
    }
    
    try {
      setProvisionSubmitting(true);
      
      const payload = {
        ...provisionForm,
        lt_employee_code: provisionForm.lt_employee_code || null
      };

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/users/provision',
        payload
      );
      
      if (response.data.success) {
        const emailNote = response.data.data?.welcome_email_sent 
          ? ' Welcome email sent.' 
          : '';
        showToast(`User ${provisionForm.email} provisioned successfully.${emailNote}`, 'success');
        setShowProvisionModal(false);
        setProvisionForm({ email: '', primary_role_id: '', authority_level_id: '', division_id: '', lt_employee_code: '', reason: '', send_welcome_email: true });
        setProvisionEmpSelected(null);
        setProvisionEmpSearch('');
        setProvisionEmpResults([]);
        fetchData();
      } else {
        throw new Error(response.data.message || 'Failed to provision user');
      }
    } catch (err) {
      console.error('Error provisioning user:', err);
      const isConflict = err.response?.status === 409;
      showToast(err.response?.data?.message || err.message, 'error');
      if (isConflict) {
        setShowProvisionModal(false);
        setProvisionForm({ email: '', primary_role_id: '', authority_level_id: '', division_id: '', lt_employee_code: '', reason: '', send_welcome_email: true });
        setProvisionEmpSelected(null);
        setProvisionEmpSearch('');
        setProvisionEmpResults([]);
        fetchData();
      }
    } finally {
      setProvisionSubmitting(false);
    }
  };

  // Update user role
  const handleUpdateRole = async (userId, newRoleId, reason = '') => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/users/${userId}/role`,
        { primary_role_id: newRoleId, reason }
      );
      
      if (response.data.success) {
        showToast('User role updated', 'success');
        fetchData();
        // Modal stays open - user can continue making changes
      } else {
        throw new Error(response.data.message || 'Failed to update role');
      }
    } catch (err) {
      console.error('Error updating role:', err);
      showToast(err.response?.data?.message || err.message, 'error');
    }
  };

  // Resend welcome email to pending user
  const handleResendWelcome = async (userId, userEmail) => {
    try {
      setResendingWelcome(userId);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/users/${userId}/resend-welcome`
      );
      
      if (response.data.success) {
        showToast(`Welcome email resent to ${userEmail}`, 'success');
      } else {
        throw new Error(response.data.message || 'Failed to resend welcome email');
      }
    } catch (err) {
      console.error('Error resending welcome email:', err);
      showToast(err.response?.data?.message || err.message || 'Failed to resend welcome email', 'error');
    } finally {
      setResendingWelcome(null);
    }
  };

  // Styles
  const styles = {
    container: {
      padding: '20px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '10px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1a1a2e',
      margin: 0
    },
    provisionBtn: {
      padding: '10px 20px',
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    filters: {
      display: 'flex',
      gap: '15px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    },
    searchInput: {
      padding: '10px 15px',
      borderRadius: '8px',
      border: '1px solid #ddd',
      fontSize: '14px',
      minWidth: '250px'
    },
    select: {
      padding: '10px 15px',
      borderRadius: '8px',
      border: '1px solid #ddd',
      fontSize: '14px',
      backgroundColor: 'white'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    },
    th: {
      padding: '15px',
      textAlign: 'left',
      backgroundColor: '#f8f9fa',
      borderBottom: '2px solid #e9ecef',
      fontWeight: '600',
      color: '#495057',
      fontSize: '13px',
      textTransform: 'uppercase'
    },
    td: {
      padding: '15px',
      borderBottom: '1px solid #e9ecef',
      fontSize: '14px',
      color: '#333'
    },
    roleBadge: (color) => ({
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '12px',
      backgroundColor: color || '#6c757d',
      color: 'white',
      fontSize: '12px',
      fontWeight: '500'
    }),
    statusBadge: (isActive) => ({
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '12px',
      backgroundColor: isActive ? '#28a745' : '#ffc107',
      color: isActive ? 'white' : '#333',
      fontSize: '12px',
      fontWeight: '500'
    }),
    actionBtn: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      backgroundColor: '#007bff',
      color: 'white',
      marginRight: '5px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '30px',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '20px',
      color: '#1a1a2e'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      color: '#333'
    },
    input: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid #ddd',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '20px'
    },
    cancelBtn: {
      padding: '10px 20px',
      borderRadius: '8px',
      border: '1px solid #ddd',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontWeight: '500'
    },
    submitBtn: {
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: '#28a745',
      color: 'white',
      cursor: 'pointer',
      fontWeight: '500'
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#666'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#666'
    },
    secondaryRoles: {
      display: 'flex',
      gap: '5px',
      flexWrap: 'wrap',
      marginTop: '5px'
    },
    secondaryBadge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '10px',
      backgroundColor: '#e9ecef',
      color: '#495057',
      fontSize: '11px'
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading users...</div>;
  }

  if (error) {
    return (
      <div style={styles.loading}>
        <p style={{ color: '#dc3545' }}>Error: {error}</p>
        <button onClick={fetchData} style={styles.actionBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>User Provisioning</h2>
        <button style={styles.provisionBtn} onClick={() => setShowProvisionModal(true)}>
          <span>+</span> Provision New User
        </button>
      </div>

      {/* Scope banner (item 6, 2026-08-10): Users = portal access ONLY.
          Person data lives on the master employee list (single source of truth). */}
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
        padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#1e40af',
      }}>
        This page manages <strong>portal access only</strong> (roles, authority, permissions).
        Person data — name, phone, department / discipline / role codes, status — lives on the
        master employee list (<strong>Accounting → Hiring / Layoff</strong>). Every user should be
        linked to a master row via <strong>Emp Code</strong>; unlinked users are flagged in the
        daily data-cleanliness email.
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Search users..."
          style={styles.searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          style={styles.select}
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
        >
          <option value="">All Departments</option>
          {departments.map(dept => (
            <option key={dept.id} value={dept.id}>{dept.name}</option>
          ))}
        </select>
        <select
          style={styles.select}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active (Logged In)</option>
          <option value="pending">Pending (Provisioned)</option>
        </select>
      </div>

      {/* Users Table */}
      {userFilter.getActiveFilters().length > 0 && (
        <div style={{ padding: '0.5rem 1rem', marginBottom: '0.5rem', backgroundColor: '#d1fae5', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
          <span style={{ color: '#065f46' }}>
            {userFilter.getActiveFilters().length} active filter(s) — showing {sortedFilteredUsers.length} of {filteredUsers.length}
          </span>
          <button onClick={() => userFilter.clearAllFilters()} style={{ padding: '2px 10px', fontSize: '0.75rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Clear All Filters</button>
        </div>
      )}
      <div style={{ overflowY: 'scroll', overflowX: 'auto', height: '360px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <table style={{ ...styles.table, borderRadius: 0, boxShadow: 'none', overflow: 'visible' }}>
        <thead>
          <tr>
            {userColumns.map(col => (
              <th key={col.key} style={{ ...styles.th, position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span onClick={() => userSort.handleSort(col.key)} style={{ cursor: 'pointer', userSelect: 'none', flex: 1 }} title={`Sort by ${col.label}`}>
                    {col.label}
                    {userSort.sortColumn === col.key && (<span style={{ fontSize: '0.7rem', marginLeft: '3px' }}>{userSort.sortDirection === 'asc' ? '▲' : '▼'}</span>)}
                  </span>
                  <span onClick={(e) => userFilter.openFilterAtPosition(e, col.key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <FilterIcon isActive={!!(userFilter.columnFilters[col.key]?.length > 0 || userFilter.filterText[col.key])} color="#666" />
                  </span>
                </div>
                {userFilter.openFilterColumn === col.key && (
                  <FilterTemplateDropdown
                    columnKey={col.key}
                    columnLabel={col.label}
                    isDateColumn={col.type === 'date'}
                    columnFilters={userFilter.columnFilters}
                    filterMode={userFilter.filterMode}
                    filterText={userFilter.filterText}
                    filterSearch={userFilter.filterSearch}
                    filterButtonPositions={userFilter.filterButtonPositions}
                    setOpenFilterColumn={userFilter.setOpenFilterColumn}
                    setFilterSearch={userFilter.setFilterSearch}
                    getUniqueValues={userFilter.getUniqueValues}
                    handleFilterToggle={userFilter.handleFilterToggle}
                    selectAllColumnFilter={userFilter.selectAllColumnFilter}
                    clearColumnFilter={userFilter.clearColumnFilter}
                    applyTextFilter={userFilter.applyTextFilter}
                  />
                )}
              </th>
            ))}
            <th style={{ ...styles.th, position: 'sticky', top: 0, zIndex: 2 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedFilteredUsers.length === 0 ? (
            <tr>
              <td colSpan={9} style={styles.emptyState}>
                No users found
              </td>
            </tr>
          ) : (
            sortedFilteredUsers.map(user => (
              <tr key={user.id}>
                <td style={styles.td}>
                  <div style={{ fontWeight: '500' }}>
                    {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Pending...'}
                  </div>
                </td>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>
                  <span style={styles.roleBadge(user.role_color || getRoleColor(user.primary_role_id))}>
                    {user.role_name || user.primary_role_id || 'No Role'}
                  </span>
                  {user.secondary_roles && user.secondary_roles.length > 0 && (
                    <div style={styles.secondaryRoles}>
                      {user.secondary_roles.map((sr, idx) => (
                        <span key={idx} style={styles.secondaryBadge}>
                          +{sr.role_name}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: user.user_authority_level ? '#fff3cd' : '#e9ecef',
                    color: user.user_authority_level ? '#856404' : '#495057',
                    border: user.user_authority_level ? '1px solid #ffc107' : '1px solid #dee2e6'
                  }}>
                    {user.user_authority_level || user.authority_level || 'Staff'}
                    {user.user_authority_level && <span title="Overridden"> *</span>}
                  </span>
                </td>
                <td style={styles.td}>{user.department_name || '-'}</td>
                <td style={styles.td}>
                  {user.lt_employee_code ? (
                    <span title={user.lt_employee_name || user.lt_employee_code} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '3px 8px', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7',
                      borderRadius: '10px', fontSize: '11px', fontWeight: '600', color: '#065f46'
                    }}>
                      🔗 {user.lt_employee_code}
                    </span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={styles.statusBadge(!!user.azure_object_id)}>
                    {user.azure_object_id ? 'Active' : 'Pending'}
                  </span>
                </td>
                <td style={styles.td}>
                  {user.last_login 
                    ? new Date(user.last_login).toLocaleDateString()
                    : '-'}
                </td>
                <td style={{...styles.td, whiteSpace: 'nowrap'}}>
                  <button
                    style={styles.actionBtn}
                    onClick={() => {
                      setSelectedUser(user);
                      setShowEditModal(true);
                    }}
                  >
                    Edit
                  </button>
                  {!user.azure_object_id && (
                    <button
                      style={{
                        ...styles.actionBtn,
                        backgroundColor: resendingWelcome === user.id ? '#6c757d' : '#17a2b8',
                        cursor: resendingWelcome === user.id ? 'wait' : 'pointer',
                        fontSize: '12px',
                        padding: '6px 10px'
                      }}
                      onClick={() => handleResendWelcome(user.id, user.email)}
                      disabled={resendingWelcome === user.id}
                      title="Resend welcome email with login instructions"
                    >
                      {resendingWelcome === user.id ? 'Sending...' : 'Resend Welcome'}
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>

      {/* Provision Modal */}
      {showProvisionModal && (
        <div style={styles.modal} onClick={() => setShowProvisionModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Provision New User</h3>
            <form onSubmit={handleProvision}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address *</label>
                <input
                  type="email"
                  style={styles.input}
                  value={provisionForm.email}
                  onChange={(e) => setProvisionForm({...provisionForm, email: e.target.value})}
                  placeholder="user@laframboise.net"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Company (Division)</label>
                <select
                  style={styles.input}
                  value={provisionForm.division_id}
                  onChange={(e) => setProvisionForm({...provisionForm, division_id: e.target.value})}
                >
                  <option value="">— Not assigned —</option>
                  {divisions.map(div => (
                    <option key={div.id} value={div.id}>
                      {div.division_number} - {div.division_name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select
                  style={styles.input}
                  value={provisionForm.primary_role_id}
                  onChange={(e) => setProvisionForm({...provisionForm, primary_role_id: e.target.value})}
                  required
                >
                  <option value="">Select a role...</option>
                  {Object.entries(rolesForEmail(rolesByDepartment, provisionForm.email)).map(([deptName, roles]) => (
                    <optgroup key={deptName} label={deptName}>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.display_name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {isExternalEmail(provisionForm.email) && (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
                    Outside e-mail: role is External. After provisioning, give them an External template on one job under Permissions → Assignments.
                  </div>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Authority Level Override (optional)</label>
                <select
                  style={styles.input}
                  value={provisionForm.authority_level_id}
                  onChange={(e) => setProvisionForm({...provisionForm, authority_level_id: e.target.value})}
                >
                  <option value="">Use role default</option>
                  {provisionAuthorityLevels.map(level => (
                    <option key={level.id} value={level.id}>
                      {level.name} - {level.description || ''}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                  Leave blank to use the default authority level from the selected role
                </p>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Reason (optional)</label>
                <input
                  type="text"
                  style={styles.input}
                  value={provisionForm.reason}
                  onChange={(e) => setProvisionForm({...provisionForm, reason: e.target.value})}
                  placeholder="e.g., New hire, contractor, etc."
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Employee Code (optional)
                  <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                    (Links user to their employee record — required for Fab Shop Foreman)
                  </span>
                </label>
                <div style={{ position: 'relative' }}>
                  {provisionEmpSelected ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        flex: 1, padding: '8px 12px', backgroundColor: '#d1fae5',
                        border: '1px solid #6ee7b7', borderRadius: '4px', fontSize: '13px', fontWeight: '600', color: '#065f46'
                      }}>
                        {provisionEmpSelected.emp_code} — {provisionEmpSelected.emp_name}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setProvisionEmpSelected(null);
                          setProvisionEmpSearch('');
                          setProvisionForm(prev => ({ ...prev, lt_employee_code: '' }));
                        }}
                        style={{ padding: '6px 10px', border: '1px solid #fca5a5', borderRadius: '4px', backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Clear
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        style={styles.input}
                        value={provisionEmpSearch}
                        onChange={(e) => handleProvisionEmpSearch(e.target.value)}
                        placeholder="Search by name or employee code..."
                      />
                      {provisionEmpResults.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                          backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '180px', overflowY: 'auto'
                        }}>
                          {provisionEmpResults.map(emp => (
                            <div
                              key={emp.emp_code}
                              onClick={() => {
                                setProvisionEmpSelected({ emp_code: emp.emp_code, emp_name: emp.emp_name });
                                setProvisionForm(prev => ({ ...prev, lt_employee_code: emp.emp_code }));
                                setProvisionEmpSearch('');
                                setProvisionEmpResults([]);
                              }}
                              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f4f6' }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              <strong>{emp.emp_code}</strong> — {emp.emp_name}
                              {emp.trade && <span style={{ color: '#6b7280', marginLeft: '6px', fontSize: '12px' }}>{emp.trade}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {provisionEmpLoading && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Searching...</div>}
                    </>
                  )}
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  <input
                    type="checkbox"
                    checked={provisionForm.send_welcome_email}
                    onChange={(e) => setProvisionForm({...provisionForm, send_welcome_email: e.target.checked})}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Send welcome email
                </label>
                <p style={{ fontSize: '11px', color: '#666', marginTop: '4px', marginLeft: '28px' }}>
                  Sends a welcome letter with login instructions to the user's email address
                </p>
              </div>
              <div style={styles.modalButtons}>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => setShowProvisionModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.submitBtn}
                  disabled={provisionSubmitting}
                >
                  {provisionSubmitting ? 'Provisioning...' : 'Provision User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced Edit User Modal */}
      {showEditModal && selectedUser && (
        <EnhancedEditModal
          user={selectedUser}
          roleTemplates={roleTemplates}
          rolesByDepartment={rolesByDepartment}
          departments={departments}
          divisions={divisions}
          styles={styles}
          onClose={() => setShowEditModal(false)}
          onUpdateRole={handleUpdateRole}
          onRefresh={() => fetchData(selectedUser.id)}
          instance={instance}
          accounts={accounts}
          showToast={showToast}
          getRoleColor={getRoleColor}
        />
      )}

      {/* Toast notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          zIndex: 99999,
          padding: '14px 20px',
          borderRadius: '8px',
          backgroundColor: toast.type === 'error' ? '#dc2626' : '#16a34a',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          maxWidth: '420px',
          lineHeight: '1.4',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px'
        }}>
          <span style={{ fontSize: '16px', flexShrink: 0 }}>
            {toast.type === 'error' ? '✕' : '✓'}
          </span>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default UserProvisioningTab;
